import { BillingDetails } from "@stripe/stripe-js";
import { WhiteLabelData } from "@web3auth/auth";

/**
 * Authenticated dashboard user derived from Auth0 (replaces Web3Auth AuthUserInfo for login).
 *
 * Field mapping:
 * - `authConnection` — dashboard login method (e.g. google, github, custom); not stored in the users table.
 * - `verifier` — canonical provider key, persisted as `users.verifier`. For standard Auth0 login this is the
 *   Web3Auth authConnectionId (e.g. auth0-google); for grouped flows such as MM wallet it is the
 *   groupedAuthConnectionId (e.g. agenticAuthGroupedConnectionId).
 * - `verifierId` — stable user identity within that verifier, persisted as `users.verifier_id` (email, OIDC sub, etc.).
 * - `sub` — raw Auth0/OIDC subject claim from the id token.
 */
export type DashboardAuthUser = {
  email?: string;
  name?: string;
  picture?: string;
  authConnection: string;
  verifier: string;
  verifierId: string;
  sub: string;
};

// VERIFIER
export type Verifier = {
  verifier_id: string;
  verifier_type: string;
  network: string;
  status: string;
  owner: string;
  has_owner: boolean;
  project_id: string | undefined;
  CreatedAt: string;
  DeletedAt: string | null;
  client_id: string;
  client_secret: string;
  auth0_authentication_type: string;
  domain: string;
  jwt_verifier_id: string;
  jwk_endpoint: string;
  jwt_validation_fields: string;
  jwt_validation_values: string;
  aggregate_verifier_params: string;
  jwt_verifier_id_case_insensitive: boolean | null;
  is_legacy: boolean;
  team_id: number;
  linked_projects?: string;
  verifier_editing_locked?: boolean;
};

export type AggregateSubVerifier = {
  verifier_id: string;
  verifier_type: string;
  // below field is no longer collected in new versions, but we still maintain the type
  // to represent older verifiers
  auth0_authentication_type?: string;
  client_id: string;
  client_secret: string;
  domain: string;
  jwt_validation_fields: string[];
  jwt_validation_values: (string | boolean)[];
  jwt_verifier_id: string;
  jwk_endpoint: string;
  jwt_verifier_id_case_insensitive?: boolean;
};

export type UpdateVerifierParams = {
  verifier_id: string;
  verifier_type?: string;
  network: string;
  params: string;
  aggregate_verifier_params?: string;
  jwt_verifier_id_case_insensitive?: boolean;
  team_id: number;
  client_id?: string;
  client_secret?: string;
  domain?: string;
  jwt_validation_fields?: string;
  jwt_validation_values?: string;
  jwt_verifier_id?: string;
  jwk_endpoint?: string;
  transaction_hash?: string;
};

export type VerifierContractParams = {
  client_id?: string;
  app_id?: string;
  app_s?: string;
  jwt_validation_fields?: string[];
  jwt_validation_values?: (string | boolean)[];
  jwt_verifier_id_field?: string;
  jwk_endpoint?: string;
  jwk_keys?: string;
  jwt_verifier_id_case_insensitive?: boolean;
};

export type JWTValidation = {
  field: string;
  value: string | boolean;
  checked?: boolean;
};

// PLAN
export type Plan = {
  order: number;
  name: string;
  title?: string;
  monthlyPrice?: string;
  yearlyPrice?: string;
  description: string;
  postfixTitle?: string;
  items: { icon: string; text: string }[];
};

export type TestAccount = {
  email: string;
  phone_number: string;
  otp_code: string;
};

/** Mirrors backend `projects.type`. */
export type ProjectType = "default" | "agentic";

// PROJECT
export type Project = {
  ID: number;
  DeletedAt: string | null;
  project_id: string;
  project_secret: string;
  name: string;
  network: string;
  platform_types: string;
  description: string;
  user_data_in_id_token: boolean;
  session_duration: number;
  products: string;
  sms_otp_enabled: boolean;
  email_passwordless_enabled: boolean;
  external_wallets_enabled: boolean;
  external_wallets_config: string | null;
  passkeys_enabled: boolean;
  wallet_connect_enabled: boolean;
  wallet_connect_project_id: string;
  whitelabel: string;
  login_modal_config: string | null;
  team_id: number;
  /** Backend `projects.type`; omitted or `"default"` is treated as a regular dashboard project. */
  type?: ProjectType;
  whitelists: number;
  chain_config: string | null;
  default_chain_id: string | null;
  verifiers: number;
  key_export_enabled: boolean;
  smart_accounts_enabled: boolean;
  smart_accounts_configuration: string | null;
  social_login_config: string | null;
  wallet_ui_config: string | null;
  test_accounts: TestAccount[];
};

export type ProjectACLLimits = {
  max_acls_per_access_type: number;
  max_entries_per_acl: number;
};

export type ProjectDetailResponse = ProjectACLLimits & {
  project: Project;
};

export type ACLAccessType = "allow" | "block" | "scope";

export type ACLEntryType = "email" | "email_domain" | "wallet" | "userId";

export type ACLEntry = {
  id: number;
  acl_id: number;
  entry_type: ACLEntryType;
  value: string;
  created_at: string;
  updated_at: string;
};

export type ACL = {
  id: number;
  project_id: string;
  name: string;
  access_type: ACLAccessType;
  enabled_at?: string;
  scope?: string;
  entries?: ACLEntry[];
  created_at: string;
  updated_at: string;
};

export type ACLListResponse = {
  acls: ACL[];
};

export type ACLDetailResponse = {
  acl: ACL;
};

export type ACLCreateResponse = {
  acl: ACL;
};

export type ACLCreateEntryResponse = {
  entry: ACLEntry;
};

export type ChainNamespace = {
  ID: number;
  chain_namespace: string;
  wallet_permission: boolean;
};

export type UpdateChainNamespace = {
  id?: number;
  chain_namespace: string;
  wallet_permission?: boolean;
  is_deleted?: boolean;
};

export type SupportedChainNamespace = {
  namespace: string;
  label: string;
  walletName?: string;
  walletLink?: string;
};

// TEAM
export type Team = {
  id: number;
  name: string;
  display_name: string;
  role: string;
  email: string;
  industry: string;
  organization_size: string;
  organization_url: string;
  description: string;
  new_formula_start_date: string;
  invoice_gate_bypassed: boolean;
};

export type TeamMember = {
  ID: number;
  user_id: number;
  team_id: number;
  role: string;
  user: {
    id: number;
    email: string;
    name: string;
  };
};

export type TeamInvite = {
  team_id: number;
  email: string;
  role: string;
  invite_code: string;
  inviter: string;
  accepted: boolean;
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  team: Team;
};

// BILLING
export type Invoice = {
  ID: number;
  CreatedAt: string;
  from: string;
  to: string;
  price: number;
  type: string;
  status: string;
  title?: string;
  payment_url: string;
  pdf: string;
  due_date: string;
  priceDisplay?: string;
  createdAt?: string;
  invoice_id: string;
  team_id: number;
  payment_status: string;
};

export type CreditCard = {
  id: string;
  brand: string;
  last4: string;
  expiryMonth: string;
  expiryYear: string;
  billingDetails: BillingDetails;
};

export type Subscription = {
  customer: {
    plan: string;
    stripe_id: string;
    subscriptions: Array<{
      stripe_id: string;
      type: string;
      customer_id: number;
    }>;
  };
  billing_email: string;
  card: CreditCard;
  coupon?: {
    name: string;
    code: string;
    metadata: Record<string, string>;
    redeemed_at: number;
    expires_at: number;
  };
};

export type SubscriptionDowngrade = {
  schedule_id: string;
  from_plan: string;
  to_plan: string;
  start_date: number;
};

export type Coupon = {
  code: string;
  amount?: number;
};

export type RedeemCouponResult = {
  coupon: {
    code: string;
  };
};

export type SubscriptionResponse = {
  client_secret: string;
  subscription_id: string;
};

export type InvoiceLinks = {
  payment_url: string;
  pdf_url: string;
};

// this is for the chainslist at https://assets.web3auth.io/v1/chains.json
export interface ChainsListRegistryChainConfigItem {
  chainId: string;
  chainNamespace: "eip155" | "solana" | "other";
  rpcTarget: string;
  wsTarget?: string;
  displayName: string;
  blockExplorerUrl: string;
  ticker: string;
  tickerName: string;
  logo: string;
  decimals: number;
  isInfuraSupported: boolean;
  isTestnet?: boolean;
}
export type ChainsListRegistryResponse = ChainsListRegistryChainConfigItem[];

export interface DBChainConfigItem {
  enabled: boolean;
  chainId: string;
  rpcTarget: string;
  wsTarget?: string;
  fallbackRpcTargets?: string[];
  fallbackWsTargets?: string[];
  isCustomNetwork?: boolean;
  isTestnet?: boolean;
  chainNamespace?: string;
  displayName?: string;
  blockExplorerUrl?: string;
  ticker?: string;
  tickerName?: string;
  logo?: string;
  decimals?: number;
}
export type DBChainConfig = Array<DBChainConfigItem>;

export interface UIChainConfigItem extends Omit<DBChainConfigItem, "fallbackRpcTargets" | "fallbackWsTargets"> {
  isInfuraSupported?: boolean;
  addWssUrl?: boolean;
  sortScore?: number;
  bundlerUrl?: string;
  paymasterUrl?: string;
  fallbackRpcTarget?: string;
  fallbackWsTarget?: string;
}
export type UIChainConfig = UIChainConfigItem[];

export const WalletScope = {
  ALL: "all",
  EMBEDDED: "embedded",
} as const;
export type WalletScope = (typeof WalletScope)[keyof typeof WalletScope];

export const EIP_STANDARD = {
  EIP_4337: "4337",
  EIP_7702: "7702",
} as const;
export type EipStandard = (typeof EIP_STANDARD)[keyof typeof EIP_STANDARD];

export const SMART_ACCOUNT_PROVIDERS = {
  metamask: "metamask",
  kernel: "kernel",
  safe: "safe",
  trust: "trust",
  nexus: "nexus",
} as const;
export type SmartAccountProviders = (typeof SMART_ACCOUNT_PROVIDERS)[keyof typeof SMART_ACCOUNT_PROVIDERS];

export type ExternalWalletsConfig = {
  disableAllRecommendedWallets: boolean;
  disableAllOtherWallets: boolean;
  disabledWallets: string[];
};

export type SmartAccountChainConfigItem = {
  chainId: string;
  bundlerConfig?: {
    url: string;
  };
  paymasterConfig?: {
    url: string;
  };
};

export type SmartAccountsConfiguration = {
  eipStandard: EipStandard;
  smartAccountType: SmartAccountProviders;
  walletScope: WalletScope;
  chains: Array<SmartAccountChainConfigItem>;
};

export type DBSmartAccountsConfiguration = Omit<SmartAccountsConfiguration, "eipStandard" | "chains"> & {
  eipStandard?: EipStandard;
  chains?: Array<SmartAccountChainConfigItem>;
};

// TODO: also omit appName?
export type WhiteLabelDataModifiedType = Omit<WhiteLabelData, "appUrl"> & {
  tncLink: string;
  privacyPolicy: string;
  // new field which is missing in older SDK versions
  consentRequired?: boolean;
};

export type LoginModalConfig = {
  widgetType: "embed" | "modal";
  logoAlignment: "left" | "center";
  borderRadiusType: "small" | "medium" | "large";
  buttonRadiusType: "pill" | "rounded" | "square";
  signInMethods: Array<"social" | "passwordless" | "externalWallets">;
  addPreviousLoginHint: boolean;
  displayInstalledExternalWallets: boolean;
  displayExternalWalletsCount: boolean;
};
